'use client';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useBranding } from '~/lib/branding';

/*
 * NO `SITE` IMPORT, DELIBERATELY — and the unit test in
 * tests/unit/tenant-brand-leak.test.ts fails if one comes back.
 *
 * `SITE` is New Leaf Cleaning's marketing content. This component renders on
 * every public page of every tenant, so anything it reads from `SITE` is New
 * Leaf's identity shown to another company's visitors. Both leaks were here at
 * once: `/images/nlc-logo.png` hard-coded as the masthead (with the TENANT's
 * name in its alt text, which is what made it read as intentional), and
 * `SITE.navLinks` offering a commercial cleaner's visitors "Recurring cleaning"
 * and "Move-in & move-out".
 *
 * Everything company-specific now comes from `useBranding()`, and an absent
 * value renders as nothing rather than as New Leaf's.
 */

/**
 * Where a portal's login page actually is.
 *
 * The canonical admin/team/client address for every tenant is now
 * <id>.broomstack.app — this used to be a bare relative link, which meant
 * "Login" only ever worked on whichever host you were already on. That is
 * fine on a tenant's own subdomain, but broken from any host that is not one
 * (the public apex on a legacy or unrecognised domain), because there is
 * nothing there for it to be relative TO.
 *
 * Falls back to relative when the tenant id could not be resolved (localhost,
 * an unknown host) — never fabricates a subdomain from a guess.
 */
function portalLoginHref(tenantId: string | null, portal: 'admin' | 'team' | 'client'): string {
  return tenantId ? `https://${tenantId}.broomstack.app/${portal}/login` : `/${portal}/login`;
}

export function PublicNav() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  // null = hide the strip entirely (no active jobs)
  const [stripMessage] = useState<string | null>(null);
  const { pathname } = useLocation();
  const { id: tenantId, name: brandName, logoUrl, navLinks, isNewLeaf } = useBranding();

  /*
   * The "someone is cleaning in X" strip used to poll the platform every five
   * minutes at /api/public/active-crew. That endpoint returns 404 and sends no
   * CORS headers, so every page load logged a CORS error plus a failed request,
   * and the strip never rendered — it has been dead, not merely quiet.
   *
   * Removed rather than repaired: the strip named individual crew members by
   * display name, and Vikelas asked for crew names to come off the public site
   * (2026-09-10). Restoring the endpoint would put them back.
   *
   * `stripMessage` stays as state so the markup below is untouched; it is simply
   * always null now, which is the "hide the strip" case the code already handles.
   */

  const closeDrawer = () => { setDrawerOpen(false); document.body.style.overflow = ''; };
  const openDrawer  = () => { setDrawerOpen(true);  document.body.style.overflow = 'hidden'; };

  return (
    <>
      {/* Live status strip — only shows when a crew member is actively clocked in */}
      {stripMessage !== null && (
        <div className="live-strip">
          <div className="live-strip-inner">
            <span className="pulse-dot" />
            <span>{stripMessage}</span>
          </div>
        </div>
      )}

      {/* Top nav */}
      <header className="topbar">
        <div className="topbar-inner">
          {/*
            The masthead. A company with no logo of its own gets its NAME set as
            a wordmark — never a stand-in mark belonging to someone else.
            `.nav-brand-name` already existed in marketing.css, unused: the
            display-serif wordmark this case was always meant to have.
          */}
          <Link to="/" className="brand-logo" aria-label={`${brandName} — home`}>
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="nav-logo-img" />
            ) : (
              <span className="nav-brand-name">{brandName}</span>
            )}
          </Link>

          {/* Desktop links — margin-left gives breathing room from the logo.
              Empty for a tenant with no marketing pages of its own, so the row
              collapses rather than linking into another company's site. */}
          {navLinks.length > 0 && (
            <nav className="nav-links nav-links-spaced" aria-label="Main navigation">
              {navLinks.map((link) => (
                <Link key={link.href} to={link.href} aria-current={pathname === link.href ? 'page' : undefined}>
                  {link.label}
                </Link>
              ))}
            </nav>
          )}

          <div className="nav-cta">
            {/* Login dropdown */}
            {/* Opened on hover only, so keyboard and touch users could never
                reach the three portals. Click now toggles it, Escape closes it,
                and moving focus out of the group closes it. */}
            <div
              className="nav-login-wrap"
              onMouseEnter={() => setLoginOpen(true)}
              onMouseLeave={() => setLoginOpen(false)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setLoginOpen(false);
              }}
              onKeyDown={(e) => { if (e.key === 'Escape') setLoginOpen(false); }}
            >
              <button
                type="button"
                className="btn btn-ghost ghost-hide nav-login-trigger"
                aria-expanded={loginOpen}
                aria-haspopup="true"
                onClick={() => setLoginOpen((v) => !v)}
              >
                Login ▾
              </button>
              {loginOpen && (
                <div className="nav-login-dropdown">
                  <a href={portalLoginHref(tenantId, 'admin')} className="nav-login-item">Admin Portal</a>
                  <a href={portalLoginHref(tenantId, 'team')} className="nav-login-item">Team Portal</a>
                  <a href={portalLoginHref(tenantId, 'client')} className="nav-login-item">Client Portal</a>
                </div>
              )}
            </div>
            <Link to="/quote" className="btn btn-brass">
              Get a quote
            </Link>
            <button
              className="nav-hamburger"
              aria-label="Open navigation"
              type="button"
              aria-expanded={drawerOpen ? 'true' : 'false'}
              onClick={openDrawer}
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {/* `inert` is what actually removes the drawer from the tab order and the
          a11y tree. It was aria-hidden alone, which hides it from screen readers
          but leaves all 12 links keyboard-focusable — so tabbing from the header
          walked into an invisible menu (axe: aria-hidden-focus, WCAG 4.1.2). */}
      <div
        className={`nav-drawer${drawerOpen ? ' open' : ''}`}
        inert={!drawerOpen}
      >
        <button type="button" className="nav-drawer-close" aria-label="Close navigation" onClick={closeDrawer}>✕</button>
        <nav className="nav-drawer-links">
          {navLinks.map((link) => (
            <Link key={link.href} to={link.href} onClick={closeDrawer}>{link.label}</Link>
          ))}
          {/* /contact and /policies are New Leaf's own pages — its address, its
              hours, its Florida governing-law terms. Offering them to another
              tenant's visitors points them at the wrong company's answers. */}
          {isNewLeaf && <Link to="/contact" onClick={closeDrawer}>Contact</Link>}
          <a href={portalLoginHref(tenantId, 'admin')} onClick={closeDrawer}>Admin Login</a>
          <a href={portalLoginHref(tenantId, 'team')} onClick={closeDrawer}>Team Login</a>
          {/* Was missing entirely from the mobile drawer — desktop has all three,
              mobile had two. Same fix, same place it belonged. */}
          <a href={portalLoginHref(tenantId, 'client')} onClick={closeDrawer}>Client Login</a>
          {isNewLeaf && (
            <Link to="/policies" onClick={closeDrawer} className="nav-drawer-policies">Policies</Link>
          )}
        </nav>
        <div className="nav-drawer-foot">
          <Link to="/quote" className="btn btn-brass btn-lg nav-drawer-cta" onClick={closeDrawer}>
            Get a quote
          </Link>
        </div>
      </div>

      <div className={`nav-overlay${drawerOpen ? ' open' : ''}`} onClick={closeDrawer} aria-hidden="true" />
    </>
  );
}
